'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';

// --- NAPRAWA BŁĘDU TYPESCRIPT ---
// Informujemy TS, że window ma funkcję gtag
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}

export default function CookieBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);

  // Stan zgód
  const [consent, setConsent] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const savedConsent = localStorage.getItem('cookie_consent');
    if (savedConsent) {
      setConsent(JSON.parse(savedConsent));
    } else {
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const updateGAConsent = (newConsent: typeof consent) => {
    // Teraz TypeScript nie będzie krzyczał o window.gtag
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': newConsent.analytics ? 'granted' : 'denied',
        'ad_storage': newConsent.marketing ? 'granted' : 'denied',
        'ad_user_data': newConsent.marketing ? 'granted' : 'denied',
        'ad_personalization': newConsent.marketing ? 'granted' : 'denied'
      });
    }
    
    // Sygnał dla Header.tsx i AssistantWidget.tsx
    window.dispatchEvent(new Event("cookie_consent_updated"));
  };

  const handleAcceptAll = () => {
    const newConsent = { necessary: true, analytics: true, marketing: true };
    setConsent(newConsent);
    localStorage.setItem('cookie_consent', JSON.stringify(newConsent));
    updateGAConsent(newConsent);
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    const newConsent = { necessary: true, analytics: false, marketing: false };
    setConsent(newConsent);
    localStorage.setItem('cookie_consent', JSON.stringify(newConsent));
    updateGAConsent(newConsent);
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    const finalConsent = { ...consent, necessary: true };
    setConsent(finalConsent);
    localStorage.setItem('cookie_consent', JSON.stringify(finalConsent));
    updateGAConsent(finalConsent);
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div 
      role="alertdialog" 
      aria-labelledby="cookie-heading" 
      className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6 bg-white border-t border-gray-200 shadow-[0_-4px_20px_rgba(0,0,0,0.1)] animate-in slide-in-from-bottom-5 duration-500 font-sans"
    >
      <div className="mx-auto max-w-7xl">
        
        {!showPreferences ? (
          // --- WIDOK GŁÓWNY ---
          <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between">
            <div className="space-y-3 flex-1">
              <h3 id="cookie-heading" className="text-lg font-bold text-gray-900 flex items-center gap-2">
                🍪 Szanujemy Twoją prywatność
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed max-w-4xl">
                Strona korzysta z plików cookies w celu realizacji usług i zgodnie z <Link href="/polityka-prywatnosci" className="text-blue-600 hover:underline font-bold">Polityką Prywatności</Link>. 
                <br className="hidden md:block" />
                Zgoda na pliki analityczne pozwoli nam wyświetlić opcję <strong>Darmowej Wyceny</strong> oraz odblokować Asystenta AI.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto shrink-0">
              <button 
                onClick={() => setShowPreferences(true)}
                className="px-6 py-3 text-sm font-bold text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition focus:ring-4 focus:ring-gray-200 focus:outline-none"
              >
                Ustawienia
              </button>
              <button 
                onClick={handleRejectAll}
                className="px-6 py-3 text-sm font-bold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition focus:ring-4 focus:ring-gray-200 focus:outline-none"
              >
                Tylko niezbędne
              </button>
              <button 
                onClick={handleAcceptAll}
                className="px-6 py-3 text-sm font-bold text-white bg-black rounded-xl hover:bg-gray-800 transition shadow-lg focus:ring-4 focus:ring-blue-300 focus:outline-none"
              >
                Akceptuj wszystkie
              </button>
            </div>
          </div>
        ) : (
          // --- WIDOK PREFERENCJI ---
          <div className="space-y-6">
            <div className="flex justify-between items-start border-b border-gray-100 pb-4">
                <div>
                    <h3 className="text-xl font-bold text-gray-900">Ustawienia preferencji</h3>
                    <p className="text-sm text-gray-500 mt-1">Wybierz, jakie pliki cookies możemy wykorzystywać.</p>
                </div>
                <button onClick={() => setShowPreferences(false)} className="text-gray-400 hover:text-gray-900 p-2 transition">
                    <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/></svg>
                </button>
            </div>

            <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-2">
                
                {/* 1. Niezbędne */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100">
                    <div className="pr-4">
                        <span className="font-bold text-gray-900 text-sm block mb-1">Niezbędne (Techniczne)</span>
                        <span className="text-xs text-gray-500 leading-snug">
                            Kluczowe funkcje strony (bezpieczeństwo, zapamiętywanie zgód). Nie można ich wyłączyć.
                        </span>
                    </div>
                    <div className="relative inline-flex items-center shrink-0">
                         <input type="checkbox" checked disabled className="sr-only peer" />
                         <div className="w-11 h-6 bg-blue-600 rounded-full peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all opacity-50 cursor-not-allowed"></div>
                    </div>
                </div>

                {/* 2. Analityczne */}
                <div className="flex items-center justify-between p-4 hover:bg-gray-50 rounded-xl border border-transparent hover:border-gray-100 transition duration-200">
                    <div className="pr-4">
                        <span className="font-bold text-gray-900 text-sm block mb-1">Analityczne i Statystyczne</span>
                        <span className="text-xs text-gray-500 leading-snug">
                            Pomagają nam ulepszać stronę. <strong>Wymagane do odblokowania przycisku "Darmowa Wycena".</strong>
                        </span>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer shrink-0">
                         <input 
                            type="checkbox" 
                            checked={consent.analytics} 
                            onChange={(e) => setConsent({...consent, analytics: e.target.checked})}
                            className="sr-only peer" 
                         />
                         <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
                    </label>
                </div>

                {/* 3. Marketingowe */}
                <div className="flex items-center justify-between p-4 hover:bg-gray-50 rounded-xl border border-transparent hover:border-gray-100 transition duration-200">
                    <div className="pr-4">
                        <span className="font-bold text-gray-900 text-sm block mb-1">Marketingowe (Reklamowe)</span>
                        <span className="text-xs text-gray-500 leading-snug">
                            Umożliwiają dopasowanie reklam Google Ads do Twoich zainteresowań.
                        </span>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer shrink-0">
                         <input 
                            type="checkbox" 
                            checked={consent.marketing} 
                            onChange={(e) => setConsent({...consent, marketing: e.target.checked})}
                            className="sr-only peer" 
                         />
                         <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
                    </label>
                </div>

            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                 <button 
                    onClick={() => setShowPreferences(false)}
                    className="px-6 py-2.5 text-sm font-bold text-gray-600 hover:text-gray-900 transition"
                  >
                    Wróć
                  </button>
                  <button 
                    onClick={handleSavePreferences}
                    className="px-8 py-2.5 text-sm font-bold text-white bg-black rounded-xl hover:bg-gray-800 transition shadow-lg focus:ring-4 focus:ring-blue-300 focus:outline-none"
                  >
                    Zapisz wybrane
                  </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}